// @flow
import * as React from 'react';
import { CloseIcon } from 'components/Common';
import classnames from 'classnames';
import styles from './Modal.scss';

type ModalHeaderProps = {
  children: React.Node,
  onCloseClick?: () => void,
};
const ModalHeader = (props: ModalHeaderProps) => {
  const { children, onCloseClick } = props;
  const heading =
    typeof children === 'string' ? (
      <h2 className="brandon f3 ma0 lh-solid">{children}</h2>
    ) : (
      children
    );

  return (
    <div className="bb b--moon-gray pa3 warm-gray">
      <div className="flex flex-row items-center">
        {heading}
        {typeof onCloseClick === 'function' && (
          <button
            type="button"
            onClick={onCloseClick}
            className="input-reset b--none bg-transparent grow-large hover-blue link pa1 pointer"
            style={{ marginLeft: 'auto' }}
          >
            <CloseIcon className="w1 h1 db blue" />
            <span className={styles.modalExitBtn}>Close modal</span>
          </button>
        )}
      </div>
    </div>
  );
};

const ModalBody = (props: { children: React.Node }) => (
  <div className="overflow-auto">{props.children}</div>
);

const ModalFooter = (props: { children: React.Node }) => (
  <div className="pa3 bt b--moon-gray warm-gray">
    <div className="flex items-center">
      <div style={{ marginLeft: 'auto' }}>{props.children}</div>
    </div>
  </div>
);

type Props = {
  onClose?: () => void,
  children: React.ChildrenArray<
    | React.Element<typeof ModalHeader>
    | React.Element<typeof ModalBody>
    | React.Element<typeof ModalFooter>
  >,
};
type State = {
  open: boolean,
};

export default class Modal extends React.PureComponent<Props, State> {
  static Header = ModalHeader;
  static Body = ModalBody;
  static Footer = ModalFooter;

  showTimeout: number;
  state = {
    open: false,
  };

  close = () => {
    this.setState({ open: false });
  };

  handleKeydown = (e: KeyboardEvent) => {
    if (e.which === 27) {
      e.stopPropagation();
      e.preventDefault();
      this.close();
    }
  };

  componentDidMount() {
    window.requestAnimationFrame(() => this.setState({ open: true }));
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    // if we are closing the modal
    if (prevState.open === true && this.state.open === false) {
      if (typeof this.props.onClose === 'function') {
        this.showTimeout = window.setTimeout(
          // $FlowFixMe
          () => requestAnimationFrame(this.props.onClose),
          301
        );
      }
    }
  }

  componentWillUnmount() {
    window.clearTimeout(this.showTimeout);
  }

  render() {
    // apply onClose to header
    const children = React.Children.map(this.props.children, child => {
      if (child && child.type === ModalHeader) {
        // $FlowFixMe
        return React.cloneElement(child, { onCloseClick: this.close });
      }
      return child;
    });
    const overlayClassName = classnames(
      'fixed left-0 right-0 bottom-0 top-0 bg-black-60 flex flex-row items-center justify-around z-max',
      styles.modal,
      {
        [styles.modalOpen]: this.state.open === true,
      }
    );
    const contentClassName = classnames(
      'ba b--light-gray bg-white shadow-4 relative flex flex-column',
      styles.modalContent,
      {
        [styles.modalContentOpen]: this.state.open === true,
      }
    );

    return (
      <div
        className={overlayClassName}
        tabIndex="0"
        onKeyDownCapture={this.handleKeydown}
        ref={c => c && c.focus()}
        role="dialog"
      >
        <div
          className={contentClassName}
          style={{ maxHeight: '80vh', maxWidth: '56em' }}
        >
          {children}
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/Modal/Modal.js